import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../supabaseClient';
import AdminPanel from './AdminPanel';

const SLOTS = [
  '09:00','09:30','10:00','10:30','11:00','11:30',
  '12:00','12:30','13:00','13:30','14:00','14:30',
  '15:00','15:30','16:00','16:30','17:00','17:30',
  '18:00','18:30','19:00',
];

const DAY_NAMES   = ['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'];
const MONTH_NAMES = ['enero','febrero','marzo','abril','mayo','junio',
                     'julio','agosto','septiembre','octubre','noviembre','diciembre'];

function toDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function isSameDay(a, b) {
  return a.getDate()===b.getDate() && a.getMonth()===b.getMonth() && a.getFullYear()===b.getFullYear();
}
function firstOfMonth(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`;
}

const PLAN_LABEL  = { Individual:'Individual', Intermedio:'Intermedio', Profesional:'Profesional' };
const HAS_STATS   = (plan) => plan === 'Intermedio' || plan === 'Profesional';
const PLAN_LIMITS = {
  Individual:  { profesionales: 1,        turnos_mes: 70 },
  Intermedio:  { profesionales: 3,        turnos_mes: Infinity },
  Profesional: { profesionales: Infinity, turnos_mes: Infinity },
};

export default function Dashboard() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  const [tenant, setTenant]             = useState(null);
  const [loadingTenant, setLoadingTenant] = useState(true);
  const [activeTab, setActiveTab]       = useState('dashboard');

  const today = new Date();
  const [selectedDate, setSelectedDate] = useState(today);
  const [turnos, setTurnos]             = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedTurno, setSelectedTurno] = useState(null);

  const [stats, setStats]               = useState(null);

  // Cancel por parte del negocio
  const [cancelando, setCancelando]     = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelSaving, setCancelSaving] = useState(false);

  // Cierres
  const [cierres, setCierres]           = useState([]);
  const [cierreForm, setCierreForm]     = useState({ fecha: toDateStr(new Date()), fecha_hasta: '', tipo: 'total', hora_cierre: '15:00' });
  const [cierreSaving, setCierreSaving] = useState(false);

  // Profesionales
  const [profesionales, setProfesionales]   = useState([]);
  const [newProfNombre, setNewProfNombre]   = useState('');
  const [profSaving, setProfSaving]         = useState(false);

  // Servicios del tenant
  const [servicios, setServicios]           = useState([]);
  const [newServNombre, setNewServNombre]   = useState('');
  const [servSaving, setServSaving]         = useState(false);

  // Modal: horarios de un profesional
  const [horarioModal, setHorarioModal]     = useState(null); // profesional | null
  const [horarioEdit, setHorarioEdit]       = useState({});   // { dia: {activo, hora_inicio, hora_fin} }
  const [horarioSaving, setHorarioSaving]   = useState(false);

  // Modal: servicios de un profesional
  const [servModal, setServModal]           = useState(null);  // profesional | null
  const [profServEdit, setProfServEdit]     = useState(new Set());
  const [servModalSaving, setServModalSaving] = useState(false);

  // --- Carga el tenant del usuario autenticado ---
  useEffect(() => {
    if (!user) return;
    supabase
      .from('tenants')
      .select('*')
      .eq('user_id', user.id)
      .single()
      .then(({ data }) => {
        setTenant(data);
        setLoadingTenant(false);
      });
  }, [user]);

  // --- Carga los turnos del día seleccionado ---
  const loadSlots = useCallback(async () => {
    if (!tenant) return;
    setLoadingSlots(true);
    setSelectedTurno(null);
    const { data } = await supabase
      .from('turnos')
      .select('*, profesionales(nombre), servicios(nombre)')
      .eq('tenant_id', tenant.id)
      .eq('fecha', toDateStr(selectedDate))
      .eq('estado', 'confirmado')
      .order('hora');
    setTurnos(data || []);
    setLoadingSlots(false);
  }, [tenant, selectedDate]);

  useEffect(() => { loadSlots(); }, [loadSlots]);

  // --- Realtime: actualiza cuando llega un turno nuevo o se cancela ---
  useEffect(() => {
    if (!tenant) return;
    const channel = supabase
      .channel('dashboard-turnos-' + tenant.id)
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'turnos',
        filter: `tenant_id=eq.${tenant.id}`,
      }, () => loadSlots())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [tenant, loadSlots]);

  // --- Estadísticas del mes (solo Intermedio/Profesional) ---
  useEffect(() => {
    if (!tenant || !HAS_STATS(tenant.plan)) return;
    const desde = firstOfMonth(today);
    supabase
      .from('turnos')
      .select('fecha, hora, estado')
      .eq('tenant_id', tenant.id)
      .gte('fecha', desde)
      .then(({ data }) => {
        if (!data) return;
        const confirmados = data.filter(t => t.estado === 'confirmado');
        const hoy = toDateStr(today);
        const turnosHoy = confirmados.filter(t => t.fecha === hoy).length;

        const slotFreq = {};
        confirmados.forEach(t => { slotFreq[t.hora] = (slotFreq[t.hora] || 0) + 1; });
        const masPopular = Object.entries(slotFreq).sort((a,b) => b[1]-a[1])[0];

        setStats({
          totalMes:   confirmados.length,
          turnosHoy,
          masPopular: masPopular ? masPopular[0] : null,
        });
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant]);

  // Carga cierres para los próximos 60 días
  const loadCierres = useCallback(async () => {
    if (!tenant) return;
    const desde = toDateStr(new Date());
    const hasta = new Date(); hasta.setDate(hasta.getDate() + 60);
    const { data } = await supabase
      .from('cierres')
      .select('*')
      .eq('tenant_id', tenant.id)
      .gte('fecha', desde)
      .lte('fecha', toDateStr(hasta))
      .order('fecha');
    setCierres(data || []);
  }, [tenant]);

  useEffect(() => { loadCierres(); }, [loadCierres]);

  // Map fechaStr → cierre para lookup rápido en el selector de días
  const cierresMap = {};
  cierres.forEach(c => { cierresMap[c.fecha] = c; });

  // Cancelar un turno desde el panel del negocio
  const handleCancelarTurno = async () => {
    if (!selectedTurno) return;
    setCancelSaving(true);
    await supabase
      .from('turnos')
      .update({ estado: 'cancelado', motivo_cancelacion: cancelReason || null })
      .eq('id', selectedTurno.id);
    await supabase.functions.invoke('send-cancellation', {
      body: {
        cliente_nombre: selectedTurno.cliente_nombre,
        cliente_email:  selectedTurno.cliente_email,
        hora:           selectedTurno.hora,
        fecha:          selectedTurno.fecha,
        negocio_nombre: tenant.nombre,
        negocio_slug:   tenant.slug,
        motivo:         cancelReason,
      },
    });
    setCancelSaving(false);
    setCancelando(false);
    setCancelReason('');
    setSelectedTurno(null);
    loadSlots();
  };

  // Agregar un cierre (con soporte de rango de fechas)
  const handleAddCierre = async () => {
    if (!cierreForm.fecha) return;
    setCierreSaving(true);
    const inicio = new Date(cierreForm.fecha + 'T00:00:00');
    const fin    = cierreForm.fecha_hasta
      ? new Date(cierreForm.fecha_hasta + 'T00:00:00')
      : inicio;
    const cur = new Date(inicio);
    while (cur <= fin) {
      await supabase.from('cierres').upsert({
        tenant_id:   tenant.id,
        fecha:       toDateStr(cur),
        hora_cierre: cierreForm.tipo === 'parcial' ? cierreForm.hora_cierre : null,
      }, { onConflict: 'tenant_id,fecha' });
      cur.setDate(cur.getDate() + 1);
    }
    setCierreSaving(false);
    loadCierres();
  };

  // Eliminar un cierre
  const handleDeleteCierre = async (id) => {
    await supabase.from('cierres').delete().eq('id', id);
    loadCierres();
  };

  // Profesionales
  const loadProfesionales = useCallback(async () => {
    if (!tenant) return;
    const { data } = await supabase
      .from('profesionales')
      .select('*')
      .eq('tenant_id', tenant.id)
      .eq('activo', true)
      .order('created_at');
    setProfesionales(data || []);
  }, [tenant]);

  useEffect(() => { loadProfesionales(); }, [loadProfesionales]);

  const handleAddProfesional = async () => {
    const nombre = newProfNombre.trim();
    if (!nombre) return;
    const limite = PLAN_LIMITS[tenant.plan]?.profesionales ?? 1;
    if (profesionales.length >= limite) return;
    setProfSaving(true);
    await supabase.from('profesionales').insert({ tenant_id: tenant.id, nombre });
    setNewProfNombre('');
    setProfSaving(false);
    loadProfesionales();
  };

  const handleDeleteProfesional = async (id) => {
    await supabase.from('profesionales').update({ activo: false }).eq('id', id);
    loadProfesionales();
  };

  // Servicios
  const loadServicios = useCallback(async () => {
    if (!tenant) return;
    const { data } = await supabase
      .from('servicios')
      .select('*')
      .eq('tenant_id', tenant.id)
      .eq('activo', true)
      .order('created_at');
    setServicios(data || []);
  }, [tenant]);

  useEffect(() => { loadServicios(); }, [loadServicios]);

  const handleAddServicio = async () => {
    const nombre = newServNombre.trim();
    if (!nombre) return;
    setServSaving(true);
    await supabase.from('servicios').insert({ tenant_id: tenant.id, nombre });
    setNewServNombre('');
    setServSaving(false);
    loadServicios();
  };

  const handleDeleteServicio = async (id) => {
    await supabase.from('servicios').update({ activo: false }).eq('id', id);
    loadServicios();
  };

  // Modal horarios
  const openHorarioModal = async (prof) => {
    const { data } = await supabase
      .from('horarios_profesional')
      .select('*')
      .eq('profesional_id', prof.id);
    const edit = {};
    for (let i = 0; i < 7; i++) {
      edit[i] = { activo: false, hora_inicio: '09:00', hora_fin: '18:00' };
    }
    (data || []).forEach(h => {
      edit[h.dia_semana] = {
        activo: true,
        hora_inicio: h.hora_inicio.substring(0, 5),
        hora_fin:    h.hora_fin.substring(0, 5),
      };
    });
    setHorarioEdit(edit);
    setHorarioModal(prof);
  };

  const saveHorarios = async () => {
    setHorarioSaving(true);
    await supabase.from('horarios_profesional').delete().eq('profesional_id', horarioModal.id);
    const rows = Object.entries(horarioEdit)
      .filter(([, v]) => v.activo)
      .map(([dia, v]) => ({
        profesional_id: horarioModal.id,
        dia_semana:     Number(dia),
        hora_inicio:    v.hora_inicio,
        hora_fin:       v.hora_fin,
      }));
    if (rows.length > 0) await supabase.from('horarios_profesional').insert(rows);
    setHorarioSaving(false);
    setHorarioModal(null);
  };

  // Modal servicios del profesional
  const openServModal = async (prof) => {
    const { data } = await supabase
      .from('profesional_servicios')
      .select('servicio_id')
      .eq('profesional_id', prof.id);
    setProfServEdit(new Set((data || []).map(r => r.servicio_id)));
    setServModal(prof);
  };

  const saveServModal = async () => {
    setServModalSaving(true);
    await supabase.from('profesional_servicios').delete().eq('profesional_id', servModal.id);
    const rows = [...profServEdit].map(sid => ({
      profesional_id: servModal.id,
      servicio_id:    sid,
    }));
    if (rows.length > 0) await supabase.from('profesional_servicios').insert(rows);
    setServModalSaving(false);
    setServModal(null);
  };

  const handleLogout = async () => { await logout(); navigate('/'); };

  // --- Días para el selector ---
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    return d;
  });

  // --- Mapa de turnos por hora para el día actual ---
  const turnoMap = {};
  turnos.forEach(t => { turnoMap[t.hora] = t; });

  // ==================== LOADING ====================
  if (loadingTenant) return (
    <div style={{ textAlign:'center', padding:'6rem', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>
      Cargando...
    </div>
  );

  // Admin sin tenant → mostrar solo panel admin
  if (!tenant && isAdmin) {
    return (
      <div style={{ maxWidth:900, margin:'0 auto', padding:'32px 24px', fontFamily:'var(--font-body)' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:28 }}>
          <div style={{ fontFamily:'var(--font-head)', fontWeight:800, fontSize:'1.25rem', color:'var(--text-primary)', letterSpacing:'-0.03em' }}>
            Panel Admin
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <div style={{ textAlign:'right' }}>
              <div style={{ fontSize:13, color:'var(--text-secondary)', fontWeight:500 }}>
                {user?.user_metadata?.nombre || user?.user_metadata?.username || user?.email}
              </div>
              <div style={{ fontSize:10, color:'var(--green-light)', fontWeight:700, letterSpacing:'.06em', marginTop:2 }}>
                ADMIN
              </div>
            </div>
            <button onClick={handleLogout} style={{
              padding:'8px 18px', borderRadius:'var(--r-full)',
              border:'1px solid var(--border)', background:'transparent',
              color:'var(--text-secondary)', fontFamily:'var(--font-body)',
              fontSize:13, fontWeight:500, cursor:'pointer',
            }}>
              Cerrar sesión
            </button>
          </div>
        </div>
        <AdminPanel />
      </div>
    );
  }

  if (!tenant) return (
    <div style={{ textAlign:'center', padding:'6rem 2rem', fontFamily:'var(--font-body)' }}>
      <div style={{ fontSize:'2rem', marginBottom:12 }}>⚠️</div>
      <p style={{ color:'var(--text-secondary)', marginBottom:24 }}>
        No encontramos un negocio asociado a tu cuenta.
      </p>
      <button onClick={handleLogout} style={{ color:'var(--green-light)', background:'none', border:'none', cursor:'pointer', fontSize:14 }}>
        Cerrar sesión
      </button>
    </div>
  );

  // ==================== RENDER ====================
  return (
    <div style={{ maxWidth:900, margin:'0 auto', padding:'32px 24px', fontFamily:'var(--font-body)' }}>

      {/* HEADER */}
      <div style={{
        display:'flex', alignItems:'center', justifyContent:'space-between',
        marginBottom:28, flexWrap:'wrap', gap:12,
      }}>
        <div style={{ display:'flex', alignItems:'center', gap:14 }}>
          <div style={{
            width:48, height:48, borderRadius:'var(--r-md)',
            background:'var(--green-bg)', border:'1px solid var(--green-border)',
            display:'flex', alignItems:'center', justifyContent:'center', fontSize:22,
          }}>
            {tenant.emoji || '📅'}
          </div>
          <div>
            <div style={{ fontFamily:'var(--font-head)', fontWeight:800, fontSize:'1.25rem', color:'var(--text-primary)', letterSpacing:'-0.03em' }}>
              {tenant.nombre}
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:3 }}>
              <span style={{
                background:'var(--green-bg)', border:'1px solid var(--green-border)',
                color:'var(--green-light)', borderRadius:'var(--r-full)', padding:'2px 10px',
                fontSize:11, fontWeight:700, letterSpacing:'.06em',
              }}>
                {PLAN_LABEL[tenant.plan] || tenant.plan}
              </span>
              {tenant.slug && (
                <a href={`/${tenant.slug}`} target="_blank" rel="noreferrer"
                  style={{ fontSize:12, color:'var(--text-muted)', textDecoration:'none' }}>
                  turnokey.com/{tenant.slug} ↗
                </a>
              )}
            </div>
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ textAlign:'right' }}>
            <div style={{ fontSize:13, color:'var(--text-secondary)', fontWeight:500 }}>
              {user?.user_metadata?.nombre || user?.user_metadata?.username || user?.email}
            </div>
            {isAdmin && (
              <div style={{ fontSize:10, color:'var(--green-light)', fontWeight:700, letterSpacing:'.06em', marginTop:2 }}>
                ADMIN
              </div>
            )}
          </div>
          <button onClick={handleLogout} style={{
            padding:'8px 18px', borderRadius:'var(--r-full)',
            border:'1px solid var(--border)', background:'transparent',
            color:'var(--text-secondary)', fontFamily:'var(--font-body)',
            fontSize:13, fontWeight:500, cursor:'pointer',
          }}>
            Cerrar sesión
          </button>
        </div>
      </div>

      {/* TABS — solo visibles para admin */}
      {isAdmin && (
        <div style={{ display:'flex', gap:8, marginBottom:24 }}>
          {[
            { id:'dashboard', label:'Mi Negocio' },
            { id:'admin',     label:'Panel Admin' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding:'8px 20px', borderRadius:'var(--r-full)',
                border: activeTab === tab.id ? '2px solid var(--green)' : '1px solid var(--border)',
                background: activeTab === tab.id ? 'var(--green-bg)' : 'transparent',
                color: activeTab === tab.id ? 'var(--green-light)' : 'var(--text-secondary)',
                fontFamily:'var(--font-body)', fontSize:13, fontWeight:600, cursor:'pointer',
                transition:'all .15s',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* PANEL ADMIN */}
      {isAdmin && activeTab === 'admin' && <AdminPanel />}

      {/* CONTENIDO NORMAL — oculto si está en tab admin */}
      {activeTab === 'dashboard' && <>

      {/* ESTADÍSTICAS — solo Intermedio/Profesional */}
      {HAS_STATS(tenant.plan) && stats && (
        <div className="stats-grid">
          {[
            { label:'Turnos este mes', value: stats.totalMes },
            { label:'Turnos hoy',       value: stats.turnosHoy },
            { label:'Horario + pedido', value: stats.masPopular || '—' },
          ].map(({ label, value }) => (
            <div key={label} style={{
              background:'var(--surface)', border:'1px solid var(--border)',
              borderRadius:'var(--r-lg)', padding:'18px 20px', boxShadow:'var(--shadow)',
            }}>
              <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.08em', color:'var(--text-muted)', marginBottom:8 }}>
                {label.toUpperCase()}
              </div>
              <div style={{ fontFamily:'var(--font-head)', fontSize:'1.8rem', fontWeight:800, color:'var(--green-light)', letterSpacing:'-0.04em' }}>
                {value}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* SELECTOR DE DÍA */}
      <div style={{
        background:'var(--surface)', border:'1px solid var(--border)',
        borderRadius:'var(--r-lg)', padding:'20px 24px', marginBottom:20,
        boxShadow:'var(--shadow)',
      }}>
        <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.08em', color:'var(--text-muted)', marginBottom:12 }}>
          ELEGÍ UN DÍA
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:8 }}>
          {days.map((d, i) => {
            const isSel    = isSameDay(d, selectedDate);
            const isToday  = isSameDay(d, today);
            const cierre   = cierresMap[toDateStr(d)];
            const cerrado  = !!cierre;
            return (
              <button key={i} onClick={() => { setSelectedDate(d); setCancelando(false); setCancelReason(''); }} style={{
                display:'flex', flexDirection:'column', alignItems:'center', gap:4,
                padding:'10px 4px', borderRadius:'var(--r-md)',
                border: isSel ? '2px solid var(--green)' : cerrado ? '1px solid #fca5a5' : '1px solid var(--border)',
                background: isSel ? 'var(--green-bg)' : cerrado ? '#fef2f2' : isToday ? 'var(--bg3)' : 'transparent',
                cursor:'pointer', transition:'all .18s', position:'relative',
              }}>
                <span style={{ fontSize:11, color: cerrado ? '#ef4444' : 'var(--text-muted)', fontWeight:600, letterSpacing:'.06em' }}>
                  {DAY_NAMES[d.getDay()]}
                </span>
                <span style={{
                  fontFamily:'var(--font-head)', fontWeight:700, fontSize:'1.1rem',
                  color: isSel ? 'var(--green-light)' : cerrado ? '#dc2626' : 'var(--text-primary)',
                }}>
                  {d.getDate()}
                </span>
                {cerrado && (
                  <span style={{ fontSize:9, color:'#ef4444', fontWeight:700, letterSpacing:'.04em' }}>
                    {cierre.hora_cierre ? `hasta ${cierre.hora_cierre.substring(0,5)}` : 'cerrado'}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* GRILLA DE TURNOS */}
      <div style={{
        background:'var(--surface)', border:'1px solid var(--border)',
        borderRadius:'var(--r-xl)', padding:'24px', boxShadow:'var(--shadow)',
      }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:18 }}>
          <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.08em', color:'var(--text-muted)' }}>
            {DAY_NAMES[selectedDate.getDay()].toUpperCase()} {selectedDate.getDate()} DE {MONTH_NAMES[selectedDate.getMonth()].toUpperCase()}
          </div>
          {!loadingSlots && (
            <span style={{ fontSize:12, color:'var(--text-secondary)' }}>
              {turnos.length} turno{turnos.length !== 1 ? 's' : ''} ocupado{turnos.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {loadingSlots ? (
          <div style={{ textAlign:'center', padding:'2rem', color:'var(--text-muted)', fontSize:14 }}>
            Cargando...
          </div>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10 }}>
            {SLOTS.map(slot => {
              const turno  = turnoMap[slot];
              const isSel  = selectedTurno?.hora === slot;
              return (
                <button
                  key={slot}
                  disabled={!turno}
                  onClick={() => setSelectedTurno(isSel ? null : turno)}
                  style={{
                    padding:'11px 0', borderRadius:'var(--r-md)', textAlign:'center',
                    border: isSel
                      ? '2px solid var(--green)'
                      : turno
                        ? '1px solid var(--green-border)'
                        : '1px solid var(--border)',
                    background: isSel
                      ? 'var(--green-bg)'
                      : turno
                        ? 'rgba(22,163,74,0.06)'
                        : 'transparent',
                    color: turno ? 'var(--green-light)' : 'var(--text-muted)',
                    fontFamily:'var(--font-body)', fontSize:14,
                    fontWeight: turno ? 600 : 400,
                    cursor: turno ? 'pointer' : 'default',
                    opacity: turno ? 1 : 0.35,
                    transition:'all .15s',
                  }}
                >
                  {slot}
                </button>
              );
            })}
          </div>
        )}

        {/* PANEL DE DATOS DEL CLIENTE */}
        {selectedTurno && (
          <div style={{
            marginTop:20, paddingTop:20, borderTop:'1px solid var(--border)',
            animation:'fadeIn .15s ease',
          }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
              <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.08em', color:'var(--text-muted)' }}>
                DATOS DEL CLIENTE — TURNO {selectedTurno.hora}
              </div>
              {!cancelando && (
                <button onClick={() => setCancelando(true)} style={{
                  padding:'6px 14px', borderRadius:'var(--r-full)',
                  border:'1px solid #fca5a5', background:'#fef2f2',
                  color:'#dc2626', fontFamily:'var(--font-body)',
                  fontSize:12, fontWeight:600, cursor:'pointer',
                }}>
                  Cancelar turno
                </button>
              )}
            </div>
            <div style={{
              display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap:12,
            }}>
              <ClientField label="Nombre"   value={selectedTurno.cliente_nombre} />
              <ClientField label="Email"    value={selectedTurno.cliente_email}  />
              {selectedTurno.cliente_telefono && (
                <ClientField label="Teléfono" value={selectedTurno.cliente_telefono} />
              )}
              {selectedTurno.profesionales?.nombre && (
                <ClientField label="Profesional" value={selectedTurno.profesionales.nombre} />
              )}
              {selectedTurno.servicios?.nombre && (
                <ClientField label="Servicio" value={selectedTurno.servicios.nombre} />
              )}
            </div>

            {/* FORM DE CANCELACIÓN */}
            {cancelando && (
              <div style={{ marginTop:16, padding:16, background:'#fef2f2', borderRadius:'var(--r-md)', border:'1px solid #fca5a5' }}>
                <div style={{ fontSize:12, fontWeight:700, color:'#dc2626', marginBottom:8 }}>
                  Cancelar turno de {selectedTurno.cliente_nombre} — {selectedTurno.hora} hs
                </div>
                <textarea
                  value={cancelReason}
                  onChange={e => setCancelReason(e.target.value)}
                  placeholder="Motivo de cancelación (opcional — se envía al cliente por email)"
                  rows={3}
                  style={{
                    width:'100%', padding:'10px 12px', borderRadius:'var(--r-md)',
                    border:'1px solid #fca5a5', background:'#fff',
                    fontFamily:'var(--font-body)', fontSize:13, color:'var(--text-primary)',
                    resize:'vertical', boxSizing:'border-box',
                  }}
                />
                <div style={{ display:'flex', gap:8, marginTop:10 }}>
                  <button
                    onClick={handleCancelarTurno}
                    disabled={cancelSaving}
                    style={{
                      padding:'8px 20px', borderRadius:'var(--r-full)',
                      background:'#dc2626', color:'#fff', border:'none',
                      fontFamily:'var(--font-body)', fontSize:13, fontWeight:600,
                      cursor: cancelSaving ? 'default' : 'pointer', opacity: cancelSaving ? .6 : 1,
                    }}
                  >
                    {cancelSaving ? 'Cancelando...' : 'Confirmar cancelación'}
                  </button>
                  <button
                    onClick={() => { setCancelando(false); setCancelReason(''); }}
                    disabled={cancelSaving}
                    style={{
                      padding:'8px 16px', borderRadius:'var(--r-full)',
                      background:'transparent', border:'1px solid var(--border)',
                      color:'var(--text-secondary)', fontFamily:'var(--font-body)',
                      fontSize:13, cursor:'pointer',
                    }}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* GESTIÓN DE CIERRES */}
      <div style={{
        background:'var(--surface)', border:'1px solid var(--border)',
        borderRadius:'var(--r-xl)', padding:'24px', boxShadow:'var(--shadow)', marginTop:20,
      }}>
        <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.08em', color:'var(--text-muted)', marginBottom:18 }}>
          DÍAS CERRADOS
        </div>

        {/* FORM AGREGAR CIERRE */}
        <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'flex-end', marginBottom:20 }}>
          <div>
            <div style={{ fontSize:11, color:'var(--text-muted)', fontWeight:600, marginBottom:4 }}>DESDE</div>
            <input
              type="date"
              value={cierreForm.fecha}
              min={toDateStr(new Date())}
              onChange={e => setCierreForm(f => ({ ...f, fecha: e.target.value, fecha_hasta: f.fecha_hasta && f.fecha_hasta < e.target.value ? e.target.value : f.fecha_hasta }))}
              style={{
                padding:'8px 12px', borderRadius:'var(--r-md)',
                border:'1px solid var(--border)', background:'var(--bg3)',
                fontFamily:'var(--font-body)', fontSize:13, color:'var(--text-primary)',
              }}
            />
          </div>
          <div>
            <div style={{ fontSize:11, color:'var(--text-muted)', fontWeight:600, marginBottom:4 }}>HASTA <span style={{ fontWeight:400, opacity:.6 }}>(opcional)</span></div>
            <input
              type="date"
              value={cierreForm.fecha_hasta}
              min={cierreForm.fecha || toDateStr(new Date())}
              onChange={e => setCierreForm(f => ({ ...f, fecha_hasta: e.target.value }))}
              style={{
                padding:'8px 12px', borderRadius:'var(--r-md)',
                border:'1px solid var(--border)', background:'var(--bg3)',
                fontFamily:'var(--font-body)', fontSize:13, color:'var(--text-primary)',
              }}
            />
          </div>
          <div>
            <div style={{ fontSize:11, color:'var(--text-muted)', fontWeight:600, marginBottom:4 }}>TIPO</div>
            <div style={{ display:'flex', gap:6 }}>
              {[{ v:'total', l:'Cierre total' }, { v:'parcial', l:'Cierre parcial' }].map(({ v, l }) => (
                <button
                  key={v}
                  onClick={() => setCierreForm(f => ({ ...f, tipo: v }))}
                  style={{
                    padding:'8px 14px', borderRadius:'var(--r-full)',
                    border: cierreForm.tipo === v ? '2px solid var(--green)' : '1px solid var(--border)',
                    background: cierreForm.tipo === v ? 'var(--green-bg)' : 'transparent',
                    color: cierreForm.tipo === v ? 'var(--green-light)' : 'var(--text-secondary)',
                    fontFamily:'var(--font-body)', fontSize:12, fontWeight:600, cursor:'pointer',
                  }}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
          {cierreForm.tipo === 'parcial' && (
            <div>
              <div style={{ fontSize:11, color:'var(--text-muted)', fontWeight:600, marginBottom:4 }}>CERRAR A LAS</div>
              <input
                type="time"
                value={cierreForm.hora_cierre}
                min="09:00"
                max="19:30"
                onChange={e => setCierreForm(f => ({ ...f, hora_cierre: e.target.value }))}
                style={{
                  padding:'8px 12px', borderRadius:'var(--r-md)',
                  border:'1px solid var(--border)', background:'var(--bg3)',
                  fontFamily:'var(--font-body)', fontSize:13, color:'var(--text-primary)',
                }}
              />
            </div>
          )}
          <button
            onClick={handleAddCierre}
            disabled={cierreSaving}
            style={{
              padding:'9px 20px', borderRadius:'var(--r-full)',
              background:'var(--green)', color:'#fff', border:'none',
              fontFamily:'var(--font-body)', fontSize:13, fontWeight:600,
              cursor: cierreSaving ? 'default' : 'pointer', opacity: cierreSaving ? .6 : 1,
            }}
          >
            {cierreSaving ? 'Guardando...' : 'Agregar cierre'}
          </button>
        </div>

        {/* LISTA DE CIERRES */}
        {cierres.length === 0 ? (
          <div style={{ fontSize:13, color:'var(--text-muted)', textAlign:'center', padding:'12px 0' }}>
            No hay días cerrados configurados.
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {cierres.map(c => {
              const [y, m, d] = c.fecha.split('-').map(Number);
              const label = `${d} de ${MONTH_NAMES[m-1]} de ${y}`;
              const desc  = c.hora_cierre
                ? `Cierre parcial — hasta las ${c.hora_cierre.substring(0,5)} hs`
                : 'Cierre total del día';
              return (
                <div key={c.id} style={{
                  display:'flex', justifyContent:'space-between', alignItems:'center',
                  padding:'10px 14px', background:'var(--bg3)',
                  borderRadius:'var(--r-md)', border:'1px solid var(--border)',
                }}>
                  <div>
                    <span style={{ fontSize:14, fontWeight:600, color:'var(--text-primary)' }}>{label}</span>
                    <span style={{ fontSize:12, color: c.hora_cierre ? '#d97706' : '#dc2626', marginLeft:12, fontWeight:500 }}>
                      {desc}
                    </span>
                  </div>
                  <button
                    onClick={() => handleDeleteCierre(c.id)}
                    style={{
                      padding:'4px 12px', borderRadius:'var(--r-full)',
                      border:'1px solid #fca5a5', background:'#fef2f2',
                      color:'#dc2626', fontSize:12, fontWeight:600,
                      cursor:'pointer', fontFamily:'var(--font-body)',
                    }}
                  >
                    Eliminar
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* GESTIÓN DE PROFESIONALES */}
      {tenant.tipo_negocio !== 'cancha_futbol' && (() => {
        const limite   = PLAN_LIMITS[tenant.plan]?.profesionales ?? 1;
        const alLimite = profesionales.length >= limite;
        return (
          <div style={{
            background:'var(--surface)', border:'1px solid var(--border)',
            borderRadius:'var(--r-xl)', padding:'24px', boxShadow:'var(--shadow)', marginTop:20,
          }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:18 }}>
              <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.08em', color:'var(--text-muted)' }}>
                PROFESIONALES
              </div>
              <span style={{ fontSize:12, color: alLimite ? '#dc2626' : 'var(--text-muted)', fontWeight:600 }}>
                {profesionales.length}/{limite === Infinity ? '∞' : limite} — Plan {tenant.plan}
              </span>
            </div>

            {/* Agregar profesional */}
            {!alLimite ? (
              <div style={{ display:'flex', gap:8, marginBottom:16 }}>
                <input
                  type="text"
                  value={newProfNombre}
                  onChange={e => setNewProfNombre(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddProfesional()}
                  placeholder="Nombre del profesional"
                  maxLength={60}
                  style={{
                    flex:1, padding:'8px 12px', borderRadius:'var(--r-md)',
                    border:'1px solid var(--border)', background:'var(--bg3)',
                    fontFamily:'var(--font-body)', fontSize:13, color:'var(--text-primary)',
                  }}
                />
                <button
                  onClick={handleAddProfesional}
                  disabled={profSaving || !newProfNombre.trim()}
                  style={{
                    padding:'8px 18px', borderRadius:'var(--r-full)',
                    background:'var(--green)', color:'#fff', border:'none',
                    fontFamily:'var(--font-body)', fontSize:13, fontWeight:600,
                    cursor: profSaving || !newProfNombre.trim() ? 'default' : 'pointer',
                    opacity: profSaving || !newProfNombre.trim() ? .5 : 1,
                  }}
                >
                  Agregar
                </button>
              </div>
            ) : (
              <div style={{ fontSize:13, color:'#dc2626', marginBottom:16, padding:'10px 14px', background:'#fef2f2', borderRadius:'var(--r-md)', border:'1px solid #fca5a5' }}>
                Límite de tu plan alcanzado ({limite} profesional{limite !== 1 ? 'es' : ''}).{' '}
                {tenant.plan !== 'Profesional' && <span style={{ fontWeight:600 }}>Actualizá tu plan para agregar más.</span>}
              </div>
            )}

            {/* Lista */}
            {profesionales.length === 0 ? (
              <div style={{ fontSize:13, color:'var(--text-muted)', textAlign:'center', padding:'12px 0' }}>
                Sin profesionales — los turnos van al negocio en general.
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {profesionales.map(p => (
                  <div key={p.id} style={{
                    display:'flex', justifyContent:'space-between', alignItems:'center',
                    padding:'10px 14px', background:'var(--bg3)',
                    borderRadius:'var(--r-md)', border:'1px solid var(--border)',
                    flexWrap:'wrap', gap:8,
                  }}>
                    <span style={{ fontSize:14, fontWeight:600, color:'var(--text-primary)' }}>{p.nombre}</span>
                    <div style={{ display:'flex', gap:6 }}>
                      <button
                        onClick={() => openHorarioModal(p)}
                        style={{
                          padding:'4px 12px', borderRadius:'var(--r-full)',
                          border:'1px solid var(--border)', background:'transparent',
                          color:'var(--text-secondary)', fontSize:12, fontWeight:600,
                          cursor:'pointer', fontFamily:'var(--font-body)',
                        }}
                      >
                        Horarios
                      </button>
                      {servicios.length > 0 && (
                        <button
                          onClick={() => openServModal(p)}
                          style={{
                            padding:'4px 12px', borderRadius:'var(--r-full)',
                            border:'1px solid var(--border)', background:'transparent',
                            color:'var(--text-secondary)', fontSize:12, fontWeight:600,
                            cursor:'pointer', fontFamily:'var(--font-body)',
                          }}
                        >
                          Servicios
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteProfesional(p.id)}
                        style={{
                          padding:'4px 12px', borderRadius:'var(--r-full)',
                          border:'1px solid #fca5a5', background:'#fef2f2',
                          color:'#dc2626', fontSize:12, fontWeight:600,
                          cursor:'pointer', fontFamily:'var(--font-body)',
                        }}
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* GESTIÓN DE SERVICIOS */}
      {tenant.tipo_negocio !== 'cancha_futbol' && (
        <div style={{
          background:'var(--surface)', border:'1px solid var(--border)',
          borderRadius:'var(--r-xl)', padding:'24px', boxShadow:'var(--shadow)', marginTop:20,
        }}>
          <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.08em', color:'var(--text-muted)', marginBottom:18 }}>
            SERVICIOS DEL NEGOCIO
          </div>
          <div style={{ display:'flex', gap:8, marginBottom:16 }}>
            <input
              type="text"
              value={newServNombre}
              onChange={e => setNewServNombre(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAddServicio()}
              placeholder="Ej: Revisión, Prótesis, Corte de cabello..."
              maxLength={60}
              style={{
                flex:1, padding:'8px 12px', borderRadius:'var(--r-md)',
                border:'1px solid var(--border)', background:'var(--bg3)',
                fontFamily:'var(--font-body)', fontSize:13, color:'var(--text-primary)',
              }}
            />
            <button
              onClick={handleAddServicio}
              disabled={servSaving || !newServNombre.trim()}
              style={{
                padding:'8px 18px', borderRadius:'var(--r-full)',
                background:'var(--green)', color:'#fff', border:'none',
                fontFamily:'var(--font-body)', fontSize:13, fontWeight:600,
                cursor: servSaving || !newServNombre.trim() ? 'default' : 'pointer',
                opacity: servSaving || !newServNombre.trim() ? .5 : 1,
              }}
            >
              Agregar
            </button>
          </div>
          {servicios.length === 0 ? (
            <div style={{ fontSize:13, color:'var(--text-muted)', textAlign:'center', padding:'12px 0' }}>
              Sin servicios configurados — podés agregar los tipos de atención que ofrecés.
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {servicios.map(s => (
                <div key={s.id} style={{
                  display:'flex', justifyContent:'space-between', alignItems:'center',
                  padding:'10px 14px', background:'var(--bg3)',
                  borderRadius:'var(--r-md)', border:'1px solid var(--border)',
                }}>
                  <span style={{ fontSize:14, fontWeight:600, color:'var(--text-primary)' }}>{s.nombre}</span>
                  <button
                    onClick={() => handleDeleteServicio(s.id)}
                    style={{
                      padding:'4px 12px', borderRadius:'var(--r-full)',
                      border:'1px solid #fca5a5', background:'#fef2f2',
                      color:'#dc2626', fontSize:12, fontWeight:600,
                      cursor:'pointer', fontFamily:'var(--font-body)',
                    }}
                  >
                    Eliminar
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <style>{`@keyframes fadeIn { from { opacity:0; transform:translateY(-4px); } to { opacity:1; transform:translateY(0); } }`}</style>

      <div style={{ textAlign:'center', marginTop:28 }}>
        <span style={{ fontSize:12, color:'var(--text-muted)' }}>
          {DAY_NAMES[today.getDay()]} {today.getDate()} de {MONTH_NAMES[today.getMonth()]} de {today.getFullYear()}
        </span>
      </div>

      </> /* fin activeTab === 'dashboard' */}

      {/* MODAL: HORARIOS DEL PROFESIONAL */}
      {horarioModal && (
        <div style={{
          position:'fixed', inset:0, background:'rgba(0,0,0,0.55)',
          display:'flex', alignItems:'center', justifyContent:'center',
          zIndex:9999, padding:16,
        }} onClick={e => { if (e.target === e.currentTarget) setHorarioModal(null); }}>
          <div style={{
            background:'var(--surface)', borderRadius:'var(--r-xl)',
            padding:28, width:'100%', maxWidth:480,
            border:'1px solid var(--border)', boxShadow:'0 8px 40px rgba(0,0,0,0.35)',
            maxHeight:'90vh', overflowY:'auto',
          }}>
            <div style={{ fontFamily:'var(--font-head)', fontWeight:800, fontSize:'1.1rem', color:'var(--text-primary)', marginBottom:6 }}>
              Horarios de {horarioModal.nombre}
            </div>
            <div style={{ fontSize:13, color:'var(--text-muted)', marginBottom:20 }}>
              Marcá los días que trabaja y el rango horario. Si no configurás horarios, se usan los del negocio.
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {[
                { dia: 1, nombre: 'Lunes' },
                { dia: 2, nombre: 'Martes' },
                { dia: 3, nombre: 'Miércoles' },
                { dia: 4, nombre: 'Jueves' },
                { dia: 5, nombre: 'Viernes' },
                { dia: 6, nombre: 'Sábado' },
                { dia: 0, nombre: 'Domingo' },
              ].map(({ dia, nombre }) => {
                const h = horarioEdit[dia] || { activo: false, hora_inicio: '09:00', hora_fin: '18:00' };
                return (
                  <div key={dia} style={{
                    display:'flex', alignItems:'center', gap:12,
                    padding:'10px 14px', borderRadius:'var(--r-md)',
                    border: h.activo ? '1px solid var(--green-border)' : '1px solid var(--border)',
                    background: h.activo ? 'var(--green-bg)' : 'var(--bg3)',
                    flexWrap:'wrap',
                  }}>
                    <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', flex:'0 0 110px' }}>
                      <input
                        type="checkbox"
                        checked={h.activo}
                        onChange={e => setHorarioEdit(prev => ({
                          ...prev,
                          [dia]: { ...prev[dia], activo: e.target.checked },
                        }))}
                        style={{ width:16, height:16, cursor:'pointer', accentColor:'var(--green)' }}
                      />
                      <span style={{ fontSize:13, fontWeight:h.activo ? 700 : 500, color: h.activo ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                        {nombre}
                      </span>
                    </label>
                    {h.activo && (
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <input
                          type="time"
                          value={h.hora_inicio}
                          onChange={e => setHorarioEdit(prev => ({
                            ...prev,
                            [dia]: { ...prev[dia], hora_inicio: e.target.value },
                          }))}
                          style={{
                            padding:'5px 8px', borderRadius:'var(--r-md)',
                            border:'1px solid var(--border)', background:'var(--surface)',
                            fontFamily:'var(--font-body)', fontSize:13, color:'var(--text-primary)',
                          }}
                        />
                        <span style={{ fontSize:12, color:'var(--text-muted)' }}>a</span>
                        <input
                          type="time"
                          value={h.hora_fin}
                          onChange={e => setHorarioEdit(prev => ({
                            ...prev,
                            [dia]: { ...prev[dia], hora_fin: e.target.value },
                          }))}
                          style={{
                            padding:'5px 8px', borderRadius:'var(--r-md)',
                            border:'1px solid var(--border)', background:'var(--surface)',
                            fontFamily:'var(--font-body)', fontSize:13, color:'var(--text-primary)',
                          }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{ display:'flex', gap:8, marginTop:22 }}>
              <button
                onClick={saveHorarios}
                disabled={horarioSaving}
                style={{
                  flex:1, padding:'10px', borderRadius:'var(--r-full)',
                  background:'var(--green)', color:'#fff', border:'none',
                  fontFamily:'var(--font-body)', fontSize:14, fontWeight:600,
                  cursor: horarioSaving ? 'default' : 'pointer', opacity: horarioSaving ? .6 : 1,
                }}
              >
                {horarioSaving ? 'Guardando...' : 'Guardar horarios'}
              </button>
              <button
                onClick={() => setHorarioModal(null)}
                disabled={horarioSaving}
                style={{
                  padding:'10px 20px', borderRadius:'var(--r-full)',
                  background:'transparent', border:'1px solid var(--border)',
                  color:'var(--text-secondary)', fontFamily:'var(--font-body)',
                  fontSize:13, cursor:'pointer',
                }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: SERVICIOS DEL PROFESIONAL */}
      {servModal && (
        <div style={{
          position:'fixed', inset:0, background:'rgba(0,0,0,0.55)',
          display:'flex', alignItems:'center', justifyContent:'center',
          zIndex:9999, padding:16,
        }} onClick={e => { if (e.target === e.currentTarget) setServModal(null); }}>
          <div style={{
            background:'var(--surface)', borderRadius:'var(--r-xl)',
            padding:28, width:'100%', maxWidth:420,
            border:'1px solid var(--border)', boxShadow:'0 8px 40px rgba(0,0,0,0.35)',
            maxHeight:'90vh', overflowY:'auto',
          }}>
            <div style={{ fontFamily:'var(--font-head)', fontWeight:800, fontSize:'1.1rem', color:'var(--text-primary)', marginBottom:6 }}>
              Servicios de {servModal.nombre}
            </div>
            <div style={{ fontSize:13, color:'var(--text-muted)', marginBottom:20 }}>
              Marcá los servicios que realiza este profesional.
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {servicios.map(s => {
                const checked = profServEdit.has(s.id);
                return (
                  <label key={s.id} style={{
                    display:'flex', alignItems:'center', gap:12, cursor:'pointer',
                    padding:'10px 14px', borderRadius:'var(--r-md)',
                    border: checked ? '1px solid var(--green-border)' : '1px solid var(--border)',
                    background: checked ? 'var(--green-bg)' : 'var(--bg3)',
                  }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        setProfServEdit(prev => {
                          const next = new Set(prev);
                          if (next.has(s.id)) next.delete(s.id);
                          else next.add(s.id);
                          return next;
                        });
                      }}
                      style={{ width:16, height:16, cursor:'pointer', accentColor:'var(--green)' }}
                    />
                    <span style={{ fontSize:14, fontWeight: checked ? 600 : 500, color: checked ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                      {s.nombre}
                    </span>
                  </label>
                );
              })}
            </div>
            <div style={{ display:'flex', gap:8, marginTop:22 }}>
              <button
                onClick={saveServModal}
                disabled={servModalSaving}
                style={{
                  flex:1, padding:'10px', borderRadius:'var(--r-full)',
                  background:'var(--green)', color:'#fff', border:'none',
                  fontFamily:'var(--font-body)', fontSize:14, fontWeight:600,
                  cursor: servModalSaving ? 'default' : 'pointer', opacity: servModalSaving ? .6 : 1,
                }}
              >
                {servModalSaving ? 'Guardando...' : 'Guardar servicios'}
              </button>
              <button
                onClick={() => setServModal(null)}
                disabled={servModalSaving}
                style={{
                  padding:'10px 20px', borderRadius:'var(--r-full)',
                  background:'transparent', border:'1px solid var(--border)',
                  color:'var(--text-secondary)', fontFamily:'var(--font-body)',
                  fontSize:13, cursor:'pointer',
                }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ClientField({ label, value }) {
  return (
    <div style={{
      background:'var(--bg3)', borderRadius:'var(--r-md)', padding:'12px 16px',
    }}>
      <div style={{ fontSize:10, fontWeight:700, letterSpacing:'.08em', color:'var(--text-muted)', marginBottom:4 }}>
        {label.toUpperCase()}
      </div>
      <div style={{ fontSize:14, color:'var(--text-primary)', fontWeight:500 }}>
        {value}
      </div>
    </div>
  );
}
